const DB_NAME = 'sales_counter_catalog_db'
const DB_VERSION = 1
const STORE_PRODUCTS = 'products'
const STORE_META = 'meta'
const META_KEY = 'catalog'

let dbPromise = null

function openDb() {
  if (dbPromise) return dbPromise

  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB no disponible'))
      return
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onupgradeneeded = event => {
      const db = event.target.result

      if (!db.objectStoreNames.contains(STORE_PRODUCTS)) {
        const products = db.createObjectStore(STORE_PRODUCTS, { keyPath: 'key' })
        products.createIndex('priceListId', 'priceListId', { unique: false })
        products.createIndex('productId', 'productId', { unique: false })
        products.createIndex('updatedAt', 'updatedAt', { unique: false })
      }

      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' })
      }
    }

    request.onsuccess = event => resolve(event.target.result)
    request.onerror = () => reject(request.error || new Error('No se pudo abrir IndexedDB'))
  })

  return dbPromise
}

function txStore(db, storeName, mode = 'readonly') {
  return db.transaction(storeName, mode).objectStore(storeName)
}

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('Operación IndexedDB fallida'))
  })
}

const catalogStorage = {
  async checkHealth() {
    try {
      await openDb()
      return { ok: true, error: '' }
    } catch(e) {
      return { ok: false, error: e.message || 'IndexedDB no disponible' }
    }
  },

  async getProduct(code, priceListId) {
    try {
      const db = await openDb()
      const store = txStore(db, STORE_PRODUCTS)
      return await requestToPromise(store.get(`${code}|${priceListId}`))
    } catch(e) {
      return null
    }
  },

  async replaceProducts(entries) {
    try {
      if (!(entries || []).length) return false

      const db = await openDb()
      const tx = db.transaction(STORE_PRODUCTS, 'readwrite')
      const store = tx.objectStore(STORE_PRODUCTS)
      store.clear()
      ;(entries || []).forEach(entry => store.put(entry))

      await new Promise((resolve, reject) => {
        tx.oncomplete = resolve
        tx.onerror = () => reject(tx.error || new Error('No se pudo guardar catálogo'))
        tx.onabort = () => reject(tx.error || new Error('Guardado de catálogo abortado'))
      })
      return true
    } catch(e) {
      return false
    }
  },

  async countProducts(priceListId) {
    try {
      const db = await openDb()
      const store = txStore(db, STORE_PRODUCTS)
      const index = store.index('priceListId')
      const items = await requestToPromise(index.getAll(parseInt(priceListId)))
      const ids = {}
      ;(items || []).forEach(item => {
        if (item.productId) ids[item.productId] = true
      })
      return Object.keys(ids).length
    } catch(e) {
      return 0
    }
  },

  async clearProducts() {
    try {
      const db = await openDb()
      const store = txStore(db, STORE_PRODUCTS, 'readwrite')
      await requestToPromise(store.clear())
      return true
    } catch(e) {
      return false
    }
  },

  async loadMeta() {
    try {
      const db = await openDb()
      const store = txStore(db, STORE_META)
      const row = await requestToPromise(store.get(META_KEY))
      return row ? row.value : null
    } catch(e) {
      return null
    }
  },

  async saveMeta(value) {
    try {
      const db = await openDb()
      const store = txStore(db, STORE_META, 'readwrite')
      await requestToPromise(store.put({ key: META_KEY, value }))
      return true
    } catch(e) {
      return false
    }
  },

  async clearMeta() {
    try {
      const db = await openDb()
      const store = txStore(db, STORE_META, 'readwrite')
      await requestToPromise(store.delete(META_KEY))
      return true
    } catch(e) {
      return false
    }
  },

  async searchProducts(priceListId, query, limit = 40) {
    try {
      const db = await openDb()
      const store = txStore(db, STORE_PRODUCTS)
      const index = store.index('priceListId')
      const items = await requestToPromise(index.getAll(parseInt(priceListId)))

      const words = (query || '').toLowerCase().trim().split(/\s+/).filter(Boolean)
      if (!words.length) return []

      const seen = new Set()
      const results = []

      for (const item of (items || [])) {
        if (!item.productId || seen.has(item.productId)) continue
        const haystack = [
          (item.product && item.product.name) || '',
          item.code || '',
          item.categoryName || '',
          item.subcategoryName || '',
        ].join(' ').toLowerCase()

        if (words.every(w => haystack.includes(w))) {
          seen.add(item.productId)
          results.push(item)
          if (results.length >= limit) break
        }
      }

      return results
    } catch(e) {
      return []
    }
  },
}

export default catalogStorage
